import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import useDmsPalette from '../hooks/useDmsPalette';

/** Per-type colour palette for known entity categories. Falls back to teal. */
const TYPE_COLORS = {
  person:       { bg: 'rgba(33,150,243,0.15)',   color: '#64b5f6' },
  organization: { bg: 'rgba(156,39,176,0.15)',   color: '#ce93d8' },
  org:          { bg: 'rgba(156,39,176,0.15)',   color: '#ce93d8' },
  location:     { bg: 'rgba(255,152,0,0.15)',    color: '#ffb74d' },
  loc:          { bg: 'rgba(255,152,0,0.15)',    color: '#ffb74d' },
  date:         { bg: 'rgba(0,200,83,0.15)',     color: '#69f0ae' },
  time:         { bg: 'rgba(0,200,83,0.15)',     color: '#69f0ae' },
  money:        { bg: 'rgba(244,67,54,0.15)',    color: '#ef9a9a' },
  currency:     { bg: 'rgba(244,67,54,0.15)',    color: '#ef9a9a' },
  default:      { bg: 'rgba(0,178,185,0.15)',    color: '#00b2c8' },
};

function resolveLabel(entity) {
  if (typeof entity === 'string') return entity;
  return entity.text || entity.name || entity.value || String(entity);
}

function resolveType(entity) {
  if (typeof entity === 'string') return 'default';
  const raw = (entity.type || entity.entityType || '').toLowerCase();
  return TYPE_COLORS[raw] ? raw : 'default';
}

/**
 * EntityTags — renders a labelled row of MUI Chips for NLP entities.
 *
 * Props:
 *   entities      – array of strings or objects (with text/type fields)
 *   onEntityClick – optional callback(label: string)
 */
export default function EntityTags({ entities, onEntityClick }) {
  const MP = useDmsPalette();
  if (!entities || entities.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      {/* Section heading */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <LabelIcon sx={{ fontSize: 14, color: MP.textSecondary }} />
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Entities
        </Typography>
      </Box>

      {/* Chips */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {entities.map((entity, i) => {
          const label = resolveLabel(entity);
          const type  = resolveType(entity);
          const { bg, color } = TYPE_COLORS[type];
          return (
            <Chip
              key={i}
              label={label}
              size="small"
              onClick={onEntityClick ? () => onEntityClick(label) : undefined}
              sx={{
                fontSize: '0.7rem',
                bgcolor: bg,
                color,
                border: `1px solid ${color}40`,
                cursor: onEntityClick ? 'pointer' : 'default',
                '&:hover': onEntityClick
                  ? { opacity: 0.82, bgcolor: bg }
                  : {},
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
